import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../template/store";
import { getSezDashboard } from "./SezActions";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";


const COLORS = {
  pend: "#f5941f",
  sub: "#3b6fe5",
  appr: "#22b573",
  rej: "#ef4444",
  auto: "#22b573",
  manual: "#6366f1",
  ink: "#0f172a",
  gray: "#64748b",
  grayLight: "#94a3b8",
  page: "#eef1f7",
};

const STATUS_COLORS: Record<string, string> = {
  Pending: COLORS.pend,
  Submitted: COLORS.sub,
  Approved: COLORS.appr,
  Rejected: COLORS.rej,
};

const STATUS_NOTES: Record<string, string> = {
  Pending: "awaiting action",
  Submitted: "under review",
  Approved: "certificate issued",
  Rejected: "needs resubmission",
};

const AGING_COLORS = ["#fcd9a8", "#f8b866", "#f5941f", "#c9660a"];

type RangeKey = "30" | "90" | "180";

const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 3 months" },
  { value: "180", label: "Last 6 months" },
];


const pct = (n: number, total: number) => (total > 0 ? (n / total) * 100 : 0);

const RANGE_TO_API: Record<RangeKey, string> = {
  "30": "30D",
  "90": "3M",
  "180": "6M",
};

export default function SezAnalyticsCharts() {
  const dispatch = useDispatch<AppDispatch>();
  const apiData = useSelector((state: RootState) => state.sezDashboard.analyticsData);
  const loading = useSelector((state: RootState) => state.sezDashboard.analyticsLoading);
  const apiError = useSelector((state: RootState) => state.sezDashboard.analyticsError);

  const [localRange, setLocalRange] = useState<RangeKey>("30");

  const range = localRange;
  const handleRange = (v: RangeKey) => setLocalRange(v);

  useEffect(() => {
    dispatch(
      getSezDashboard({
        dateRange: RANGE_TO_API[range],
        topPayorsPage: 1,
        topPayorsPageSize: 20,
        certificatesPage: 1,
        certificatesPageSize: 20,
      })
    );
  }, [dispatch, range]);

  const statusData = useMemo(() => {
    if (!apiData) return [];
    const { labels, data } = apiData.charts.status_donut;
    return labels.map((label, i) => ({
      name: label,
      value: data[i] ?? 0,
      color: STATUS_COLORS[label] ?? COLORS.gray,
      note: STATUS_NOTES[label] ?? "",
    }));
  }, [apiData]);

  const statusTotal = apiData?.kpis.total ?? 0;

  const trendData = useMemo(() => {
    if (!apiData) return [];
    const { labels, pending, submitted, approved, rejected } = apiData.charts.monthly_trend;
    return labels.map((label, i) => ({
      month: label,
      Pending: pending[i] ?? 0,
      Submitted: submitted[i] ?? 0,
      Approved: approved[i] ?? 0,
      Rejected: rejected[i] ?? 0,
    }));
  }, [apiData]);

  const agingData = useMemo(() => {
    if (!apiData) return [];
    const { labels, data } = apiData.charts.aging_buckets;
    return labels.map((label, i) => ({
      bucket: label,
      value: data[i] ?? 0,
      color: AGING_COLORS[i] ?? COLORS.pend,
    }));
  }, [apiData]);

  const approvalRaw = useMemo(() => {
    if (!apiData) return { auto: 0, manual: 0, total: 0 };
    const auto = apiData.kpis.auto_approved;
    const manual = apiData.kpis.manually_approved;
    return { auto, manual, total: auto + manual };
  }, [apiData]);

  const approvalDonutData = useMemo(() => [
    { name: "Auto-approved",    value: approvalRaw.auto,   color: COLORS.auto},
    { name: "Manually approved", value: approvalRaw.manual, color: COLORS.manual },
  ], [approvalRaw]);

  const mismatchData = useMemo(() => {
    if (!apiData) return [];
    const { labels, data } = apiData.charts.mismatch_by_field;
    return labels.map((label, i) => ({ field: label, value: data[i] ?? 0 }));
  }, [apiData]);

  if (loading && !apiData) {
    return (
      <div style={s.page}>
        <div style={{ ...s.container, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          <span style={{ fontSize: 14, color: COLORS.gray }}>Loading analytics…</span>
        </div>
      </div>
    );
  }

  if (apiError && !apiData) {
    return (
      <div style={s.page}>
        <div style={{ ...s.container, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          <span style={{ fontSize: 14, color: COLORS.rej }}>{apiError}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.headerRow}>
          <h1 style={s.h1}>SEZ Certificate Analytics</h1>
          <PillSelect value={range} onChange={handleRange} />
        </div>

        <div style={s.row}>
          <Card flex="3 1 540px">
            <CardHeader title="Requests by Status" />
            <div style={s.donutRow}>
              <div style={{ position: "relative", width: 170, height: 170, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="72%"
                      outerRadius="100%"
                      paddingAngle={statusData.filter((d) => d.value > 0).length > 1 ? 3 : 0}
                      stroke="none"
                    >
                      {statusData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div style={s.donutCenter}>
                  <span style={s.donutCenterLabel}>TOTAL REQUESTS</span>
                  <span style={s.donutCenterValue}>{statusTotal}</span>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                {statusData.map((d) => (
                  <LegendRow
                    key={d.name}
                    color={d.color}
                    name={d.name}
                    value={`${d.value}`}
                    valueNote={`${pct(d.value, statusTotal).toFixed(1)}%`}
                  />
                ))}
              </div>
            </div>
          </Card>

          <Card flex="2 1 360px">
            <CardHeader title="Approval Mix" />
            {approvalRaw.total > 0 ? (
              <div style={s.donutRow}>
                <div style={{ position: "relative", width: 170, height: 170, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={approvalDonutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="72%"
                        outerRadius="100%"
                        paddingAngle={approvalDonutData.filter((d) => d.value > 0).length > 1 ? 3 : 0}
                        stroke="none"
                      >
                        {approvalDonutData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={s.donutCenter}>
                    <span style={s.donutCenterLabel}>TOTAL</span>
                    <span style={s.donutCenterValue}>{approvalRaw.total}</span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  {approvalDonutData.map((d) => (
                    <LegendRow
                      key={d.name}
                      color={d.color}
                      name={d.name}
                      value={`${d.value}`}
                      valueNote={`${pct(d.value, approvalRaw.total).toFixed(1)}%`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div style={s.emptyState}>No approvals for the selected period</div>
            )}
          </Card>
        </div>

        {/* ── Row 2: monthly trend ──────────────────────────────────────── */}
        <div style={s.row}>
          <Card>
            <CardHeader title="Monthly Trend by Status" />
            <div style={{ ...s.chartBox, height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid vertical={false} stroke="#f1f4f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={{ stroke: "#e7ebf3" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Pending" stackId="s" fill={COLORS.pend} />
                  <Bar dataKey="Submitted" stackId="s" fill={COLORS.sub} />
                  <Bar dataKey="Approved" stackId="s" fill={COLORS.appr} />
                  <Bar dataKey="Rejected" stackId="s" fill={COLORS.rej} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* ── Row 3: aging + mismatch ───────────────────────────────────── */}
        <div style={s.row}>
          <Card minWidth={360}>
            <CardHeader title="Pending — Aging Buckets" />
            <div style={{ ...s.chartBox, height: 210 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid horizontal={false} stroke="#f1f4f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="bucket" type="category" tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={false} tickLine={false} width={82} />
                  <Tooltip />
                  <Bar dataKey="value" name="Pending requests" radius={[0, 8, 8, 0]}>
                    {agingData.map((d) => (
                      <Cell key={d.bucket} fill={d.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card minWidth={360}>
            <CardHeader title="Mismatch by Field"  />
            <div style={{ ...s.chartBox, height: 210 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mismatchData} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid horizontal={false} stroke="#f1f4f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="field" type="category" tick={{ fontSize: 11, fill: COLORS.gray }} axisLine={false} tickLine={false} width={92} />
                  <Tooltip />
                  <Bar dataKey="value" name="Mismatched requests" fill="#f5941f" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

   
   
      </div>
    </div>
  );
}


function PillSelect({ value, onChange }: { value: RangeKey; onChange: (v: RangeKey) => void }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      style={{
        ...s.pillSelectWrap,
        boxShadow: hovered ? "0 4px 14px rgba(59,111,229,.18)" : "0 2px 8px rgba(59,111,229,.10)",
        transform: hovered ? "translateY(-1px)" : "translateY(0)",
        transition: "box-shadow 0.18s ease, transform 0.18s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* calendar icon */}
      <svg
        style={{ position: "absolute", left: 13, pointerEvents: "none", flexShrink: 0 }}
        width="14" height="14" viewBox="0 0 16 16" fill="none"
      >
        <rect x="1" y="3" width="14" height="12" rx="2.5" stroke="#3b6fe5" strokeWidth="1.5" fill="none"/>
        <path d="M1 6.5h14" stroke="#3b6fe5" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M5 1.5v2M11 1.5v2" stroke="#3b6fe5" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="5.5" cy="10" r="1" fill="#3b6fe5"/>
        <circle cx="8" cy="10" r="1" fill="#3b6fe5"/>
        <circle cx="10.5" cy="10" r="1" fill="#3b6fe5"/>
      </svg>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value as RangeKey)}
        style={s.pillSelect}
      >
        {RANGE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* chevron icon */}
      <svg
        style={{ position: "absolute", right: 12, pointerEvents: "none" }}
        width="10" height="10" viewBox="0 0 10 10" fill="none"
      >
        <path d="M2 3.5L5 6.5L8 3.5" stroke="#3b6fe5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function Card({ minWidth, flex, children }: { minWidth?: number; flex?: string; children: React.ReactNode }) {
  return (
    <div style={{ ...s.card, flex: flex ?? (minWidth ? `1 1 ${minWidth}px` : "1 1 100%"), minWidth: 0 }}>
      {children}
    </div>
  );
}

function CardHeader({ title, pill }: { title: string; pill?: string }) {
  return (
    <div style={s.cardHeaderRow}>
      <div style={s.cardTitle}>{title}</div>
      {pill && <span style={s.headerPill}>{pill}</span>}
    </div>
  );
}

function LegendRow({
  color,
  name,
  value,
  valueNote,
}: {
  color: string;
  name: string;
  value: string;
  valueNote: string;
}) {
  return (
    <div style={s.legendRowItem}>
      <span style={{ ...s.legendDot, backgroundColor: color }} />
      <div style={{ flex: 1 }}>
        <div style={s.legendName}>{name}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={s.legendValue}>{value}</div>
        <div style={s.legendNote}>{valueNote}</div>
      </div>
    </div>
  );
}


const s: Record<string, CSSProperties> = {
  page: {
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
    background: COLORS.page,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: COLORS.ink,
  },
  container: { width: "100%", padding: "12px 16px 24px", boxSizing: "border-box" },
  headerRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  h1: { fontSize: 20, fontWeight: 800, color: COLORS.ink, margin: 0, letterSpacing: -0.2 },

  pillSelectWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    border: "1.5px solid #c7d8fc",
    background: "#ffffff",
  },
  pillSelect: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "transparent",
    color: "#3b56d1",
    fontWeight: 700,
    fontSize: 12.5,
    border: "none",
    borderRadius: 999,
    padding: "9px 34px 9px 34px",
    cursor: "pointer",
    outline: "none",
    letterSpacing: 0.1,
  },

  row: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 },

  card: {
    background: "#ffffff",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 6px 20px rgba(15,23,42,.06)",
    boxSizing: "border-box",
    minWidth: 0,
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  cardTitle: { fontSize: 15.5, fontWeight: 800, color: COLORS.ink },
  headerPill: {
    flexShrink: 0,
    background: "#f1f4f9",
    color: "#5b6b85",
    fontSize: 11,
    fontWeight: 700,
    padding: "6px 12px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },

  donutRow: { display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" },
  donutCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  donutCenterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.grayLight,
    letterSpacing: 0.5,
  },
  donutCenterValue: { fontSize: 28, fontWeight: 800, color: COLORS.ink, marginTop: 2 },

  legendRowItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #f1f4f9",
  },
  legendDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0, display: "inline-block" },
  legendName: { fontSize: 13, fontWeight: 700, color: COLORS.ink },
  legendNote: { fontSize: 11, color: COLORS.grayLight, marginTop: 1 },
  legendValue: { fontSize: 14, fontWeight: 800, color: COLORS.ink },

  chartBox: { position: "relative", width: "100%" },
  emptyState: {
    minHeight: 170,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.grayLight,
    fontSize: 13,
    textAlign: "center",
  },

};
